
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { roleNavigationItems } from "@/components/dashboard/sidebar/constants";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const MobileNavigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Fetch user profile to get role
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('users')
        .select('role_types')
        .eq('id', user.id)
        .single();
        
      if (error) {
        console.error("Error fetching profile:", error);
        return null;
      }
      
      return data;
    },
    enabled: !!user?.id,
  });

  const userRole = profile?.role_types?.[0] || "Patron";
  const navigationItems = roleNavigationItems[userRole as keyof typeof roleNavigationItems];

  const handleNavigation = (href: string) => {
    navigate(href);
    setIsOpen(false);
  };

  return (
    <div className="md:hidden">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="sm" className="text-foreground hover:text-primary">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent 
          side="left" 
          className="w-[280px] sm:w-[320px] glass-card border-border/50 transition-all duration-300 ease-out"
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between py-4 border-b border-border/50">
              <h2 className="text-lg font-heading font-semibold text-gradient">Navigation</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="text-foreground hover:text-primary"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <nav className="flex-1 py-4">
              <div className="space-y-2">
                <Button
                  variant={location.pathname === "/dashboard" ? "noche" : "ghost"}
                  className="w-full justify-start font-body hover:bg-accent/10"
                  onClick={() => handleNavigation("/dashboard")}
                >
                  <navigationItems.icon className="mr-2 h-4 w-4" />
                  Dashboard
                </Button>
                
                {navigationItems.subItems.map((item) => (
                  <Button
                    key={item.href}
                    variant={location.pathname === item.href ? "noche" : "ghost"}
                    className="w-full justify-start font-body hover:bg-accent/10"
                    onClick={() => handleNavigation(item.href)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </Button>
                ))}
              </div>
            </nav>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
